import React, { useState, useRef } from 'react';
import ImageResolution from '../../../../../components/imageresolutionsimple/imageresolutionsimple';
const ImageResizer = () => {
    const [uploadedImage, setUploadedImage] = useState(null);
    const [resizedImageData, setResizedImageData] = useState(null);
    const [originalWidth, setOriginalWidth] = useState(null);
    const [originalHeight, setOriginalHeight] = useState(null);
    const [newWidth, setNewWidth] = useState(1920);
    const [newHeight, setNewHeight] = useState(1080);
    const [backgroundType, setBackgroundType] = useState('transparent');
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [shouldRescale, setShouldRescale] = useState(false);
    const [keepAspectRatio, setKeepAspectRatio] = useState(true);
    const [isResizing, setIsResizing] = useState(false);
    const fileInputRef = useRef(null);
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        // Check if the file is an image
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setUploadedImage(event.target.result);
                setResizedImageData(null);
                // Create a temporary image to get dimensions
                const img = new Image();
                img.onload = () => {
                    setOriginalWidth(img.width);
                    setOriginalHeight(img.height);
                };
                img.src = event.target.result;
            }
        };
        reader.readAsDataURL(file);
    };
    const handleResizeCanvas = async () => {
        if (!uploadedImage)
            return;
        setIsResizing(true);
        try {
            // Create a canvas to resize the canvas while keeping image centered
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Could not get canvas context');
            }
            const img = new Image();
            img.onload = () => {
                // Set canvas dimensions to the new size
                canvas.width = newWidth;
                canvas.height = newHeight;
                // Fill background based on selection
                if (backgroundType === 'solid') {
                    ctx.fillStyle = backgroundColor;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
                // If transparent, we don't need to fill anything - it's already transparent by default
                // Calculate position to center the image on the new canvas
                const x = (newWidth - img.width) / 2;
                const y = (newHeight - img.height) / 2;
                // Draw the original image centered on the new canvas or rescaled to fit if requested
                if (shouldRescale) {
                    // Rescale image to fit the new canvas dimensions while maintaining aspect ratio
                    if (keepAspectRatio) {
                        // Calculate the scaled size that fits within the new canvas while maintaining aspect ratio
                        const scale = Math.min(newWidth / img.width, newHeight / img.height);
                        const scaledWidth = img.width * scale;
                        const scaledHeight = img.height * scale;
                        const scaledX = (newWidth - scaledWidth) / 2;
                        const scaledY = (newHeight - scaledHeight) / 2;
                        ctx.drawImage(img, scaledX, scaledY, scaledWidth, scaledHeight);
                    }
                    else {
                        // Rescale image to fit the new canvas dimensions without maintaining aspect ratio
                        ctx.drawImage(img, 0, 0, newWidth, newHeight);
                    }
                }
                else {
                    // Keep original size and center it on the new canvas
                    ctx.drawImage(img, x, y);
                }
                // Get the resized image data URL
                const resizedImageDataUrl = canvas.toDataURL('image/png');
                setResizedImageData(resizedImageDataUrl);
            };
            img.onerror = () => {
                throw new Error('Failed to load image for resizing');
            };
            // Set the source of the image to be resized
            img.src = uploadedImage;
        }
        catch (error) {
            console.error('Resizing error:', error);
            alert('Error resizing canvas');
        }
        finally {
            setIsResizing(false);
        }
    };
    const handleDownload = () => {
        if (!resizedImageData)
            return;
        // Get the original file name
        const originalFileName = (fileInputRef.current?.files?.[0]?.name || 'image.png').split('.')[0];
        // Remove existing resolution pattern from filename if present
        const cleanFileName = originalFileName.replace(/-\d+x\d+$/, '');
        // Create new filename with resolution
        const newFileName = `${cleanFileName}-${newWidth}x${newHeight}.png`;
        const link = document.createElement('a');
        link.href = resizedImageData;
        link.download = newFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    return (React.createElement("div", { className: "card w-100 border-0 rounded-0" },
        React.createElement("div", { className: "card-header rounded-0" },
            React.createElement("h2", { className: "card-title mb-0" }, "Image Resizer")),
        React.createElement("div", { className: "row g-4 mx-3 mb-3 mt-3" },
            React.createElement("div", { className: "col-12 col-lg-5" },
                React.createElement("div", { className: "mb-3" },
                    React.createElement("input", { type: "file", accept: "image/*", onChange: handleFileChange, ref: fileInputRef, className: "form-control" }),
                    React.createElement("p", { className: "mt-2 text-muted small" }, "Upload an image to resize it or to increase it's canvas")),
                uploadedImage && (React.createElement("div", { className: "mb-3" },
                    React.createElement("h5", null, "Original Image"),
                    React.createElement("img", { src: uploadedImage, alt: "Original preview", style: { maxWidth: '200px', maxHeight: '200px' }, className: "border rounded" }),
                    originalWidth !== null && originalHeight !== null && (React.createElement("p", { className: "mt-2 text-muted small" },
                        "Original size: ",
                        originalWidth,
                        "\u00D7",
                        originalHeight,
                        "px")))),
                uploadedImage && (React.createElement("div", { className: "mb-3" },
                    React.createElement("h5", null, "Canvas Size"),
                    React.createElement("label", { htmlFor: "resolutionSelect", className: "form-label" }, "Resolution:"),
                    React.createElement(ImageResolution, { resolution: { width: newWidth, height: newHeight }, onResolutionChange: (width, height) => {
                            setNewWidth(width);
                            setNewHeight(height);
                        } }))),
                uploadedImage && (React.createElement("div", { className: "mb-3" },
                    React.createElement("h5", null, "Background"),
                    React.createElement("div", { className: "form-check" },
                        React.createElement("input", { type: "radio", id: "transparentBackground", name: "backgroundType", checked: backgroundType === 'transparent', onChange: () => setBackgroundType('transparent'), className: "form-check-input" }),
                        React.createElement("label", { htmlFor: "transparentBackground", className: "form-check-label" }, "Transparent")),
                    React.createElement("div", { className: "form-check mb-3" },
                        React.createElement("input", { type: "radio", id: "solidBackground", name: "backgroundType", checked: backgroundType === 'solid', onChange: () => setBackgroundType('solid'), className: "form-check-input" }),
                        React.createElement("label", { htmlFor: "solidBackground", className: "form-check-label" }, "Solid Color")),
                    backgroundType === 'solid' && (React.createElement("div", { className: "mb-3" },
                        React.createElement("label", { htmlFor: "colorPicker", className: "form-label" }, "Background Color"),
                        React.createElement("input", { type: "color", id: "colorPicker", value: backgroundColor, onChange: (e) => setBackgroundColor(e.target.value), className: "form-control form-control-color" }))))),
                uploadedImage && (React.createElement("div", { className: "mb-3" },
                    React.createElement("div", { className: "form-check" },
                        React.createElement("input", { type: "checkbox", id: "rescaleCheckbox", checked: shouldRescale, onChange: (e) => setShouldRescale(e.target.checked), className: "form-check-input" }),
                        React.createElement("label", { htmlFor: "rescaleCheckbox", className: "form-check-label" }, "Rescale image to fit new canvas")),
                    shouldRescale && (React.createElement("div", { className: "form-check mt-2" },
                        React.createElement("input", { type: "checkbox", id: "keepAspectRatioCheckbox", checked: keepAspectRatio, onChange: (e) => setKeepAspectRatio(e.target.checked), className: "form-check-input" }),
                        React.createElement("label", { htmlFor: "keepAspectRatioCheckbox", className: "form-check-label" }, "Maintain original aspect ratio"))))),
                uploadedImage && (React.createElement("button", { onClick: handleResizeCanvas, disabled: isResizing, className: "btn btn-primary me-2 w-100" }, isResizing ? 'Resizing...' : 'Resize'))),
            React.createElement("div", { className: "col-12 col-lg-7" },
                React.createElement("h2", { className: "h5 fw-semibold text-secondary mb-3" }, "Resized Image"),
                React.createElement("div", { className: "border border-2 border-secondary-subtle rounded-3 p-4 text-center min-vh-50 d-flex align-items-center justify-content-center", style: { minHeight: 400 } }, resizedImageData ? (React.createElement("div", { className: "w-100" },
                    React.createElement("img", { src: resizedImageData, alt: "Resized preview", className: "img-fluid rounded shadow mb-3", style: { maxHeight: 350 } }),
                    React.createElement("br", null),
                    React.createElement("button", { onClick: handleDownload, className: "btn btn-success d-inline-flex align-items-center gap-2" }, "Download Resized Image"))) : (React.createElement("div", { className: "text-muted w-100" },
                    React.createElement("p", null, "Your resized image will appear here"))))))));
};
export default ImageResizer;
